/* ═══════════════════════════════════════════════════════════════════════════════
   Project Images Tour (v2.5.0)
   
   Feature update tour for the new project image system:
   - Airbnb-style image carousel on project cards
   - Image upload during project creation (NewProject page)
   - Image upload/management from project edit mode (Overview tab)
   - Drag-and-drop multi-upload, reorder, set cover image
   
   Pages: 'projects' (carousel visible on card grid)
   ═══════════════════════════════════════════════════════════════════════════════ */

import type { TourDefinition } from './types';

export const projectImagesTour: TourDefinition = {
  id: 'project_images_v2_5',
  version: '2.5.0',
  type: 'feature_update',
  audience: 'all',
  page: 'projects',
  priority: 2,
  title: 'Project Images',
  subtitle: 'Add site photos to your projects for visual context.',

  steps: [
    // ── Step 0: Welcome ──────────────────────────────────────────────
    {
      id: 'welcome',
      type: 'welcome',
      title: 'Project Photos',
      description: 'Project cards now support image galleries, perfect for site photos, progress shots, or architect renders.',
      icon: '📸',
      actionLabel: 'Show Me',
    },

    // ── Step 1: Image Carousel on Cards ──────────────────────────────
    {
      id: 'image-carousel',
      type: 'spotlight',
      targetSelector: '[data-tour-id="projects-image-carousel"]',
      position: 'right',
      title: 'Image Carousel',
      description: 'Each project card now shows an image carousel at the top. Use the arrows or swipe to browse photos. The first image becomes the cover.',
      icon: '🖼️',
      actionLabel: 'Next',
      spotlightPadding: 4,
    },

    // ── Step 2: Upload Photos ────────────────────────────────────────
    {
      id: 'upload-images',
      type: 'info_card',
      title: 'Adding Photos',
      description: 'You can upload images in two ways:\n\n• **New Project**: drag-and-drop photos during project creation\n• **Edit Project**: open a project\'s Overview tab and use the image uploader to add, reorder, or remove photos',
      icon: '⬆️',
      actionLabel: 'Got It',
    },

    // ── Step 3: Completion ───────────────────────────────────────────
    {
      id: 'completion',
      type: 'completion',
      title: 'Ready to Go!',
      description: 'Start adding photos to your projects for visual context and easier identification.',
      icon: '🎉',
      actionLabel: 'Start Exploring',
    },
  ],
};
